/**
 * Ledger Health Card Component
 *
 * Displays ledger ingestion health statistics including:
 * - Latest ledger index and timestamp
 * - Gap analysis for various ranges (100, 1K, 10K, 100K ledgers)
 * - Visual indicators for health status
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { LedgerHealthStats } from '../../types/admin.js';

interface LedgerHealthCardProps {
  stats: LedgerHealthStats;
}

export function LedgerHealthCard({ stats }: LedgerHealthCardProps) {
  const { latestLedger, lastTransaction, gaps } = stats;

  // The widest window is a superset of the narrower ones, so it is the honest
  // "are we missing anything?" signal. Keying status off last100 alone reports
  // "healthy" while an older gap sits unnoticed further back.
  const totalMissing = gaps.last100K;
  const hasGaps = totalMissing > 0;

  // Determine health status based on gaps
  const getGapStatus = (gapCount: number, range: number) => {
    if (gapCount === 0) return 'healthy';
    if (gapCount < range * 0.01) return 'warning'; // Less than 1% missing
    return 'critical'; // More than 1% missing
  };

  const gapItems = [
    { label: 'Last 100', count: gaps.last100, range: 100 },
    { label: 'Last 1K', count: gaps.last1K, range: 1000 },
    { label: 'Last 10K', count: gaps.last10K, range: 10000 },
    { label: 'Last 100K', count: gaps.last100K, range: 100000 },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'critical':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return '✓';
      case 'warning':
        return '⚠';
      case 'critical':
        return '✗';
      default:
        return '•';
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Ledger Ingestion Health</h2>
        <div className="text-xs sm:text-sm text-gray-500">
          Updated {latestLedger.relativeTime}
        </div>
      </div>

      {/* Latest Ledger Info */}
      <div className="mb-4 sm:mb-6 rounded-md bg-gray-50 p-3 sm:p-4">
        <div className="mb-2 text-xs sm:text-sm font-medium text-gray-500">Latest Ledger</div>
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4">
          <div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">
              {latestLedger.index.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">Ledger Index</div>
          </div>
          <div className="flex-1">
            <div className="text-xs sm:text-sm text-gray-700">
              {new Date(latestLedger.closeTime).toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">{latestLedger.relativeTime}</div>
          </div>
        </div>
      </div>

      {/* Last NFT Transaction */}
      {lastTransaction && (
        <div className={`mb-4 sm:mb-6 rounded-md p-3 sm:p-4 ${
          lastTransaction.isStale
            ? 'bg-red-50 border border-red-200'
            : 'bg-gray-50'
        }`}>
          <div className="mb-2 text-xs sm:text-sm font-medium text-gray-500">Last NFT Transaction</div>
          <div className="flex items-center gap-3">
            <div className={`text-lg sm:text-xl font-bold ${
              lastTransaction.isStale ? 'text-red-700' : 'text-gray-900'
            }`}>
              {lastTransaction.relativeTime}
            </div>
            {lastTransaction.isStale && (
              <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
                STALE
              </span>
            )}
          </div>
          {lastTransaction.time && (
            <div className="mt-1 text-xs text-gray-500">
              {new Date(lastTransaction.time).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Gap Analysis */}
      <div>
        <div className="mb-3 text-xs sm:text-sm font-medium text-gray-700">Gap Analysis</div>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          {gapItems.map((item) => {
            const status = getGapStatus(item.count, item.range);
            const statusColor = getStatusColor(status);

            return (
              <div
                key={item.label}
                className={`rounded-lg border p-2 sm:p-3 ${
                  status === 'healthy'
                    ? 'border-green-200 bg-green-50'
                    : status === 'warning'
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600">{item.label}</span>
                  <span
                    className={`text-base sm:text-lg ${
                      status === 'healthy'
                        ? 'text-green-600'
                        : status === 'warning'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}
                  >
                    {getStatusIcon(status)}
                  </span>
                </div>
                <div
                  className={`mt-1 text-lg sm:text-xl font-bold ${
                    status === 'healthy'
                      ? 'text-green-900'
                      : status === 'warning'
                      ? 'text-yellow-900'
                      : 'text-red-900'
                  }`}
                >
                  {item.count}
                </div>
                <div className="text-xs text-gray-500">missing</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Overall Health Indicator */}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 rounded-md bg-gray-50 p-3">
        <div className="text-xs sm:text-sm font-medium text-gray-700">Status:</div>
        <div
          className={`rounded-full px-3 py-1 text-xs font-semibold w-fit ${
            !hasGaps
              ? 'bg-green-100 text-green-800'
              : gaps.last100 === 0
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {!hasGaps
            ? 'Healthy - No Gaps'
            : gaps.last100 === 0
            ? `${totalMissing.toLocaleString()} Missing - Older Gaps`
            : 'Critical - Recent Gaps'}
        </div>

        {hasGaps && (
          <Link
            to="/admin/ledger-health"
            className="w-fit rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 sm:ml-auto"
          >
            Review &amp; recover &rarr;
          </Link>
        )}
      </div>
    </div>
  );
}
